import { getUpdateCheckInterval, getVersionCheckUrl, isProductionMode } from './config';
import { VersionInfo, getCurrentVersionInfo, isUpdateRequired } from './version';

export interface UpdateCheckResult {
  hasUpdate: boolean;
  isForceUpdate: boolean;
  serverVersion?: VersionInfo;
  currentVersion: VersionInfo;
  error?: string;
}

export interface UpdateDetectionOptions {
  onUpdateAvailable?: (result: UpdateCheckResult) => void;
  onForceUpdate?: (result: UpdateCheckResult) => void;
  onError?: (error: string) => void;
}

class UpdateDetectionService {
  private intervalId: NodeJS.Timeout | null = null;
  private isChecking = false;
  private options: UpdateDetectionOptions = {};

  /**
   * Start automatic update detection
   */
  public start(options: UpdateDetectionOptions = {}): void {
    this.options = options;
    
    // Only start in production mode
    if (!isProductionMode()) {
      console.log('Update detection disabled in non-production mode');
      return;
    }

    // Initial check
    this.checkForUpdates();

    // Set up interval
    const interval = getUpdateCheckInterval();
    this.intervalId = setInterval(() => {
      this.checkForUpdates();
    }, interval);

    console.log(`Update detection started with ${interval}ms interval`);
  }

  /**
   * Stop automatic update detection
   */
  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('Update detection stopped');
    }
  }

  /**
   * Manually check for updates
   */
  public async checkForUpdates(): Promise<UpdateCheckResult> {
    if (this.isChecking) {
      console.log('Update check already in progress');
      return this.getCurrentResult();
    }

    this.isChecking = true;

    try {
      const result = await this.performUpdateCheck();
      
      // Handle results
      if (result.hasUpdate) {
        this.options.onUpdateAvailable?.(result);
      }
      
      if (result.isForceUpdate) {
        this.options.onForceUpdate?.(result);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Update check failed:', errorMessage);
      this.options.onError?.(errorMessage);
      
      return {
        hasUpdate: false,
        isForceUpdate: false,
        currentVersion: getCurrentVersionInfo(),
        error: errorMessage
      };
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Perform the actual update check
   */
  private async performUpdateCheck(): Promise<UpdateCheckResult> {
    const currentVersion = getCurrentVersionInfo();
    const versionUrl = getVersionCheckUrl();

    try {
      const response = await fetch(versionUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const serverVersion: VersionInfo = await response.json();
      
      // Validate server version
      if (!serverVersion.version) {
        throw new Error('Invalid server version response');
      }

      const hasUpdate = isUpdateRequired(currentVersion.version, serverVersion.version);
      const isForceUpdate = hasUpdate && serverVersion.forceUpdate === true;

      const result: UpdateCheckResult = {
        hasUpdate,
        isForceUpdate,
        serverVersion,
        currentVersion
      };

      console.log('Update check result:', {
        current: currentVersion.version,
        server: serverVersion.version,
        hasUpdate,
        isForceUpdate
      });

      return result;
    } catch (error) {
      throw new Error(`Failed to check for updates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get current result without checking
   */
  private getCurrentResult(): UpdateCheckResult {
    return {
      hasUpdate: false,
      isForceUpdate: false,
      currentVersion: getCurrentVersionInfo()
    };
  }

  /**
   * Check if update detection is running
   */
  public isRunning(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Get current status
   */
  public getStatus(): { isRunning: boolean; isChecking: boolean } {
    return {
      isRunning: this.isRunning(),
      isChecking: this.isChecking
    };
  }
}

// Export singleton instance
export const updateDetectionService = new UpdateDetectionService();

/**
 * Start update detection with options
 */
export function startUpdateDetection(options: UpdateDetectionOptions = {}): void {
  updateDetectionService.start(options);
}

/**
 * Stop update detection
 */
export function stopUpdateDetection(): void {
  updateDetectionService.stop();
}

/**
 * Check for updates manually
 */
export function checkForUpdates(): Promise<UpdateCheckResult> {
  return updateDetectionService.checkForUpdates();
}

/**
 * Get update detection status
 */
export function getUpdateDetectionStatus(): { isRunning: boolean; isChecking: boolean } {
  return updateDetectionService.getStatus();
} 